/**
 * Floating "scroll to top" button.
 *
 * Appears in the bottom-right corner after the user scrolls down past
 * `threshold` pixels (default 400). Smooth-scrolls back to top on click.
 * Coral accent matches the HiBFF investor palette; chevron icon makes the
 * affordance obvious.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';

export const ScrollToTopButton = ({ threshold = 400, color = '#F99B7D' }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  const handleClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll to top of page"
      data-testid="scroll-to-top-button"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 50,
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: color,
        color: 'white',
        border: '3px solid white',
        boxShadow: '0 8px 20px rgba(249, 155, 125, 0.45), 0 2px 6px rgba(42, 34, 53, 0.18)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.85)',
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.25s ease-out, transform 0.25s ease-out, box-shadow 0.2s ease-out',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px) scale(1.06)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(249, 155, 125, 0.55), 0 2px 6px rgba(42, 34, 53, 0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(249, 155, 125, 0.45), 0 2px 6px rgba(42, 34, 53, 0.18)';
      }}
    >
      <ChevronUp size={22} strokeWidth={2.5} />
    </button>
  );
};

export default ScrollToTopButton;
